import { useState } from "react";
import { ChevronDown, Gauge, AlertTriangle } from "lucide-react";
import { Progress } from "./ui/progress";

const MIN_ATTEMPTS = 5;
const tone = (v) => (v >= 80 ? "text-green-600" : v >= 60 ? "text-amber-600" : "text-red-600");
const bar = (v) => (v >= 80 ? "[&>div]:bg-green-500" : v >= 60 ? "[&>div]:bg-amber-500" : "[&>div]:bg-red-500");

function TrackGroup({ track, rows, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);
  const attempts = rows.reduce((a, r) => a + r.attempts, 0);
  const correct = rows.reduce((a, r) => a + Math.round((r.accuracy / 100) * r.attempts), 0);
  const avg = attempts ? Math.round((correct / attempts) * 100) : 0;
  const scored = attempts >= MIN_ATTEMPTS;
  const sorted = [...rows].sort((a, b) => a.accuracy - b.accuracy || b.attempts - a.attempts);
  const visible = showAll ? sorted : sorted.slice(0, 4);
  const weakest = sorted[0];
  const slug = track.replace(/[^a-z0-9]/gi, "-").toLowerCase();
  return (
    <div className={`border rounded-xl overflow-hidden ${scored ? "border-slate-200" : "border-dashed border-slate-200"}`} data-testid={`track-group-${slug}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50 transition-colors" data-testid={`track-group-toggle-${slug}`}>
        <div className="min-w-0">
          <p className={`font-heading text-lg ${scored ? "text-slate-900" : "text-slate-500"}`}>{track}</p>
          <p className="text-xs text-slate-500 truncate">
            {attempts === 0 ? "Not started · optional — only if it's on your path" : scored ? `${attempts} answered · ${rows.length} section${rows.length === 1 ? "" : "s"}${weakest && weakest.accuracy < 70 ? ` · weakest: ${weakest.category}` : ""}` : `${attempts} answered · answer ${MIN_ATTEMPTS - attempts} more to see your score`}
          </p>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          {scored ? <span className={`font-heading text-2xl font-bold ${tone(avg)}`} data-testid={`track-avg-${slug}`}>{avg}%</span> : <span className="text-xs font-semibold text-slate-400" data-testid={`track-avg-${slug}`}>{attempts === 0 ? "Not started" : "Warming up"}</span>}
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 space-y-3 bg-slate-50/60">
          {attempts === 0 && <p className="text-sm text-slate-500 italic">Nothing here yet. Head to Study if this track is part of your plan — it won't affect your other scores.</p>}
          {visible.filter((c) => c.attempts > 0).map((c) => (
            <div key={c.category} data-testid={`category-row-${c.category.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-700 flex items-center gap-1.5">{scored && c.accuracy < 60 && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}{c.category} <span className="text-slate-400 text-xs">({c.attempts})</span></span>
                <span className={`font-heading font-bold ${scored ? tone(c.accuracy) : "text-slate-400"}`}>{c.accuracy}%</span>
              </div>
              <Progress value={c.accuracy} className={`h-1.5 ${scored ? bar(c.accuracy) : "[&>div]:bg-slate-300"}`} />
            </div>
          ))}
          {sorted.length > 4 && (
            <button onClick={() => setShowAll((s) => !s)} className="text-xs font-semibold text-primary hover:underline" data-testid={`track-show-all-${slug}`}>
              {showAll ? "Show fewer" : `Show all ${sorted.length} sections`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function CategoryPerformance({ categories }) {
  const groups = {};
  categories.forEach((c) => { (groups[c.track || "Other"] = groups[c.track || "Other"] || []).push(c); });
  const order = Object.entries(groups).map(([t, rows]) => {
    const attempts = rows.reduce((a, r) => a + r.attempts, 0);
    const correct = rows.reduce((a, r) => a + Math.round((r.accuracy / 100) * r.attempts), 0);
    return [t, rows, attempts >= MIN_ATTEMPTS ? correct / attempts : 2 + (attempts === 0 ? 1 : 0)];
  }).sort((a, b) => a[2] - b[2]);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-6" data-testid="category-performance">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Gauge className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-xl text-slate-900">Performance by Section</h3>
        </div>
        <p className="text-xs text-slate-400 hidden sm:block">Only the tracks you study count · tap to expand</p>
      </div>
      <div className="space-y-3">
        {order.map(([t, rows], i) => <TrackGroup key={t} track={t} rows={rows} defaultOpen={i === 0 && rows.reduce((a, r) => a + r.attempts, 0) >= MIN_ATTEMPTS} />)}
      </div>
    </div>
  );
}
